import { Close, Upload, Delete } from "@mui/icons-material";
import LockIcon from "@mui/icons-material/Lock";
import TuneIcon from "@mui/icons-material/Tune";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import BusinessIcon from "@mui/icons-material/Business";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  LinearProgress,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import { useEffect, useState, useRef } from "react";
import capture, { identifyUserCompany } from "../../capture";
import LoadingButton from "../../components/LoadingButton";
import { COMPANY_SIZES, INDUSTRIES } from "../../constants";
import {
  FeatureName,
  SELECTABLE_FEATURES,
  getFeatureDisplayName,
  FEATURES,
} from "../../constants/features";
import { Company } from "../../types";
import { hostname, parseError, uniqueId, validateURL } from "../../utils";
import { createCompany } from "../../utils/companyCreation";
import { buildCompanyDescriptionPrompt } from "../../utils/companyPrompts";
import { useWhitelabel } from "../../context/WhitelabelContext";
import { isFreeUser } from "../../utils/subscriptionUtils";
import { useEmployeeTerminology } from "../../utils/terminology";
import PopOutTextInput from "../../components/PopOutTextInput";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-edit-tabpanel-${index}`}
      aria-labelledby={`company-edit-tab-${index}`}
      style={{
        flex: 1,
        overflow: "auto",
        display: value === index ? "block" : "none",
      }}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const emptyCompany = (): Company => {
  return {
    uid: "",
    id: uniqueId(),
    name: "",
    type: "",
    description: "",
    website: "",
    industry: "",
    size: "",
    location: "",
    logoUrl: "",
    employees: [],
  };
};

export default ({
  open,
  onClose,
  onSuccess,
  selectedCompany,
  chatApi,
  eventListener,
  currentUser,
  getToken,
  companyLimits,
  currentPlan,
  availableFeatures,
  isOwner = false,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (company: Company) => void;
  selectedCompany: Company | null;
  chatApi: any;
  eventListener: Function;
  currentUser: User | undefined;
  getToken: Function;
  companyLimits?: {
    maxCompanies: number;
    currentSeats: number;
    canCreate: boolean;
    upgradeRequired: boolean;
  };
  currentPlan?: string;
  availableFeatures?: string[];
  isOwner?: boolean;
}) => {
  const { whitelabelSettings } = useWhitelabel();
  const { employees } = useEmployeeTerminology();
  const [newCompany, setNewCompany] = useState<Company>(emptyCompany());
  const [modalLoading, setModalLoading] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [descLoading, setDescLoading] = useState(false);
  const [websiteError, setWebsiteError] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tab state - only used when editing
  const [currentTab, setCurrentTab] = useState(0);
  const [settingsError, setSettingsError] = useState<string>("");

  // Features state
  const displayFeatures = availableFeatures || SELECTABLE_FEATURES;
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Credit Quota state
  const [quotaEnabled, setQuotaEnabled] = useState(false);
  const [creditLimit, setCreditLimit] = useState<string>("");
  const [currentUsage, setCurrentUsage] = useState<number>(0);

  // Security state
  const [zeroDataRetention, setZeroDataRetention] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);

  const isEditing = !!selectedCompany;
  const showSettingsTabs = isEditing && isOwner;

  // Check if whitelabel has default public employees configured
  const hasDefaultPublicEmployees = (whitelabelSettings?.defaultPublicEmployeeIds?.length ?? 0) > 0;

  const createCompanyHandler = async (retry: number | undefined = 0) => {
    if (!validForm) return;

    // Check company limits before creating
    if (companyLimits && !companyLimits.canCreate) {
      eventListener({
        type: "SET_ERROR",
        error: `You've reached your company limit of ${companyLimits.maxCompanies}. ${
          isFreeUser(currentPlan)
            ? "Upgrade to a paid plan to create more companies."
            : "Contact support to increase your company limit or upgrade your plan."
        }`,
      });
      return;
    }

    setModalLoading(true);

    try {
      // Use shared company creation logic
      // Priority: if whitelabel has default public employees, use those instead of auto-generating
      const result = await createCompany({
        getToken,
        companyData: newCompany,
        currentUser,
        chatApi,
        whitelabelSettings,
        onStatusUpdate: setStatusText,
        createEmployees: !hasDefaultPublicEmployees, // Only auto-create if no default public employees
        employeeCount: 3,
        useDefaultPublicEmployees: hasDefaultPublicEmployees, // Use default public employees when configured
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      // Track company creation
      capture("Create company", {});
    } catch (error) {
      capture("Create company failed", newCompany);
      if (retry < 3) {
        createCompanyHandler(retry + 1);
        return;
      } else {
        throw error;
      }
    }

    capture("Create company success", {});
    onSuccess(newCompany);
    handleClose();
  };

  const validForm = () => {
    return newCompany.name && newCompany.industry && newCompany.website;
  };

  const generateDescription = async () => {
    setDescLoading(true);
    try {
      const prompt = buildCompanyDescriptionPrompt({
        name: newCompany.name,
        industry: newCompany.industry,
        website: newCompany.website,
      });
      const description = await chatApi.syncCompletion(prompt, true);
      setNewCompany({ ...newCompany, description: description });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
    setDescLoading(false);
  };

  const saveCompany = async (company: Company) => {
    const currentToken = await getToken();
    await axios
      .post(`${hostname}/companies`, company, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const editCompany = async () => {
    // Validate credit limit if quota is enabled (only for owners)
    if (showSettingsTabs && quotaEnabled) {
      const limit = parseFloat(creditLimit);
      if (isNaN(limit) || limit < 0) {
        setSettingsError("Please enter a valid credit limit (0 or greater)");
        setCurrentTab(2); // Switch to Credit Quota tab
        return;
      }
    }

    setModalLoading(true);
    setSettingsError("");

    try {
      // Build company object with all settings
      let companyToSave = { ...newCompany };

      // Add settings if owner
      if (showSettingsTabs) {
        // Features
        let featuresToSave: string[] | null;
        if (selectedFeatures.length === displayFeatures.length) {
          featuresToSave = null;
        } else {
          featuresToSave = [
            ...selectedFeatures,
            FEATURES.COLLABORATORS,
            FEATURES.INTEGRATIONS,
          ];
        }
        companyToSave.allowedFeatures = featuresToSave;

        // Credit quota
        companyToSave.creditLimit = quotaEnabled ? parseFloat(creditLimit) : null;

        // Security
        companyToSave.zeroDataRetention = zeroDataRetention;
        companyToSave.mfaRequired = mfaRequired;
      }

      // Save all company data in one request
      await saveCompany(companyToSave);

      eventListener({
        type: "SET_SUCCESS",
        message: "Company updated successfully",
      });
    } catch (err) {
      setSettingsError(parseError(err));
      setModalLoading(false);
      return;
    }

    setModalLoading(false);
    handleClose();
  };

  const handleWebsiteChange = (e: any) => {
    const website = e.target.value;
    if (validateURL(website)) {
      setWebsiteError("");
    } else {
      setWebsiteError(
        "Invalid URL format. Make sure it includes the protocol (http/https)."
      );
    }
    setNewCompany({ ...newCompany, website });
  };

  const handleLogoUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setUploadError("Please upload an image file (PNG, JPG, etc.)");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setUploadError("Image size should be less than 2MB");
      return;
    }

    setUploadingLogo(true);
    setUploadError(null);

    try {
      const token = await getToken();

      // Convert image to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64String = reader.result as string;

          // Create image element to get dimensions
          const img = new Image();
          img.onload = async () => {
            const imageData = {
              name: `${newCompany.name || "Company"} Logo`,
              category: "company-logo",
              url: base64String,
              mimeType: file.type,
              width: img.width.toString(),
              height: img.height.toString(),
              fileSize: file.size.toString(),
              public: true,
            };

            // Upload the image to platform-images
            const uploadResponse = await axios.post(
              `${hostname}/platform-images`,
              imageData,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (uploadResponse.status === 201) {
              const responseData = uploadResponse.data;
              // Use publicUrl from backend response (respects whitelabel domains)
              const publicUrl = responseData.publicUrl || responseData.url;
              setNewCompany({ ...newCompany, logoUrl: publicUrl });
              setUploadingLogo(false);
            }
          };
          img.src = base64String;
        } catch (err) {
          console.error("Error uploading logo:", err);
          setUploadError("Failed to upload image. Please try again.");
          setUploadingLogo(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Error reading file:", err);
      setUploadError("Failed to read image file. Please try again.");
      setUploadingLogo(false);
    } finally {
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveLogo = () => {
    setNewCompany({ ...newCompany, logoUrl: "" });
    setUploadError(null);
  };

  // Features handlers
  const handleFeatureToggle = (feature: string) => {
    setSelectedFeatures((prev) => {
      if (prev.includes(feature)) {
        const newFeatures = prev.filter((f) => f !== feature);
        setSelectAll(false);
        return newFeatures;
      } else {
        const newFeatures = [...prev, feature];
        setSelectAll(newFeatures.length === displayFeatures.length);
        return newFeatures;
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFeatures([]);
      setSelectAll(false);
    } else {
      setSelectedFeatures(displayFeatures);
      setSelectAll(true);
    }
  };

  const loadSettings = () => {
    if (!selectedCompany || !showSettingsTabs) return;

    // Load features from company object
    let features =
      selectedCompany.allowedFeatures !== null &&
      selectedCompany.allowedFeatures !== undefined
        ? selectedCompany.allowedFeatures
        : displayFeatures;
    features = features.filter((f: string) => displayFeatures.includes(f));
    setSelectedFeatures(features);
    setSelectAll(features.length === displayFeatures.length);

    // Load credit quota from company object
    const hasQuota = selectedCompany.creditLimit !== null && selectedCompany.creditLimit !== undefined;
    setQuotaEnabled(hasQuota);
    setCreditLimit(hasQuota ? String(selectedCompany.creditLimit) : "");
    setCurrentUsage(selectedCompany.creditUsage || 0);

    // Load security settings from company object
    setZeroDataRetention(selectedCompany.zeroDataRetention || false);
    setMfaRequired(selectedCompany.mfaRequired || false);
  };

  const handleClose = () => {
    setNewCompany(emptyCompany());
    setModalLoading(false);
    setStatusText("");
    setCurrentTab(0);
    setSettingsError("");
    setSelectedFeatures([]);
    setSelectAll(false);
    setQuotaEnabled(false);
    setCreditLimit("");
    setCurrentUsage(0);
    setZeroDataRetention(false);
    onClose();
  };

  useEffect(() => {
    if (selectedCompany) {
      setNewCompany(selectedCompany);
    }
  }, [selectedCompany]);

  // Load settings when dialog opens for editing
  useEffect(() => {
    if (open && showSettingsTabs) {
      loadSettings();
    }
  }, [open, showSettingsTabs]);

  const usagePercentage =
    quotaEnabled && creditLimit
      ? Math.min((currentUsage / parseFloat(creditLimit)) * 100, 100)
      : 0;

  const renderBasicInfoForm = () => (
    <>
      {!selectedCompany && (
        <Typography variant="body1" style={{ marginTop: 10 }}>
          Provide a name, type and description for the new company. This
          information will be used to generate your AI employees and train them
          for company relevant answers.
        </Typography>
      )}

      {companyLimits && companyLimits.upgradeRequired && (
        <Box
          sx={{
            bgcolor: "warning.light",
            color: "warning.contrastText",
            p: 2,
            borderRadius: 1,
            mb: 2,
          }}
        >
          <Typography variant="body2">
            <strong>Company Limit Reached:</strong> You've reached your limit of{" "}
            {companyLimits.maxCompanies} companies.
            {isFreeUser(currentPlan)
              ? " Upgrade to a paid plan to create more companies."
              : " Contact support to increase your company limit or upgrade your plan."}
          </Typography>
        </Box>
      )}
      <TextField
        style={{ marginTop: 20 }}
        fullWidth
        value={newCompany.name}
        label="Name"
        required
        onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
      />
      <TextField
        select
        style={{ marginTop: 20 }}
        fullWidth
        value={newCompany.industry}
        label="Industry"
        required
        onChange={(e) =>
          setNewCompany({
            ...newCompany,
            industry: e.target.value,
            type: e.target.value,
          })
        }
      >
        {INDUSTRIES.map((industry) => (
          <MenuItem key={industry} value={industry}>
            {industry}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        style={{ marginTop: 20 }}
        value={newCompany.website}
        fullWidth
        onChange={(e) =>
          setNewCompany({ ...newCompany, website: e.target.value })
        }
        label="Website"
        error={!!websiteError}
        placeholder="https://www.example.com"
        helperText={websiteError}
        required
      />
      <TextField
        select
        style={{ marginTop: 20 }}
        fullWidth
        value={newCompany.size}
        label="Company Size"
        placeholder="1-10"
        required
        onChange={(e) => setNewCompany({ ...newCompany, size: e.target.value })}
      >
        {COMPANY_SIZES.map((size) => (
          <MenuItem key={size} value={size}>
            {size}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        style={{ marginTop: 20 }}
        fullWidth
        value={newCompany.location}
        label="Location"
        onChange={(e) =>
          setNewCompany({ ...newCompany, location: e.target.value })
        }
        placeholder="City, Country"
        required
      />
      <PopOutTextInput
        sx={{ mt: 2 }}
        value={newCompany.description}
        minRows={5}
        maxRows={8}
        multiline
        fullWidth
        onChange={(value) =>
          setNewCompany({ ...newCompany, description: value })
        }
        label="Description"
        required
        dialogTitle="Company Description"
        dialogDescription="Provide a detailed description of the company. This will be used to train AI employees."
      />
      <LoadingButton
        loading={descLoading}
        style={{ marginTop: 5 }}
        onClick={() => generateDescription()}
      >
        Generate Description
      </LoadingButton>

      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Company Logo (Optional)
        </Typography>
        <Card variant="outlined" sx={{ p: 2 }}>
          {newCompany.logoUrl ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <img
                src={/^https?:\/\/[a-zA-Z0-9]/.test(newCompany.logoUrl) ? newCompany.logoUrl : ""}
                alt="Company logo"
                style={{
                  maxHeight: 60,
                  maxWidth: 120,
                  objectFit: "contain",
                }}
              />
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                onClick={handleRemoveLogo}
                disabled={uploadingLogo}
                size="small"
                color="error"
              >
                <Delete />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 2 }}>
              {uploadingLogo ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Button
                    startIcon={<Upload />}
                    onClick={handleLogoUploadClick}
                    variant="outlined"
                    disabled={uploadingLogo}
                    size="small"
                  >
                    Upload Logo
                  </Button>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 1 }}
                    color="text.secondary"
                  >
                    PNG, JPG up to 2MB - Suggested size: 200x200
                  </Typography>
                </>
              )}
            </Box>
          )}
          {uploadError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, display: "block" }}
            >
              {uploadError}
            </Typography>
          )}
        </Card>
      </Box>

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </>
  );

  const renderFeaturesTab = () => (
    <>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Select which features are available for this company. These restrictions
        will override individual collaborator settings.
      </Typography>

      <FormControlLabel
        control={<Checkbox checked={selectAll} onChange={handleSelectAll} />}
        label={<strong>Select All (No Restrictions)</strong>}
        sx={{ mb: 1 }}
      />

      <FormGroup>
        {displayFeatures.map((feature) => (
          <FormControlLabel
            key={feature}
            control={
              <Checkbox
                checked={selectedFeatures.includes(feature)}
                onChange={() => handleFeatureToggle(feature)}
              />
            }
            label={getFeatureDisplayName(feature as FeatureName, employees)}
          />
        ))}
      </FormGroup>

      <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Note:</strong> If no features are selected, all features will
          be restricted. Only features available in your subscription tier are
          shown.
        </Typography>
      </Box>
    </>
  );

  const renderCreditQuotaTab = () => (
    <>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Set a monthly credit limit for this company. When the limit is reached,
        operations will be blocked until the next month or the quota is
        increased.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={quotaEnabled}
            onChange={(e) => setQuotaEnabled(e.target.checked)}
          />
        }
        label="Enable credit quota for this company"
        sx={{ mb: 2 }}
      />

      {quotaEnabled && (
        <>
          <TextField
            label="Monthly Credit Limit"
            type="number"
            value={creditLimit}
            onChange={(e) => setCreditLimit(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ min: 0 }}
            helperText="Set the maximum credits this company can use per month"
          />

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Month Usage: {currentUsage.toFixed(1)} /{" "}
              {creditLimit || "∞"} credits
            </Typography>
            <LinearProgress
              variant="determinate"
              value={usagePercentage}
              color={
                usagePercentage >= 90
                  ? "error"
                  : usagePercentage >= 70
                  ? "warning"
                  : "primary"
              }
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography
              variant="caption"
              color={usagePercentage >= 90 ? "error" : "text.secondary"}
            >
              {usagePercentage.toFixed(1)}% used
            </Typography>
          </Box>
        </>
      )}

      <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Note:</strong> When disabled, this company will use the
          account owner's credit limit. Company quota is checked before the
          account limit, so you can use this to allocate credits across multiple
          companies.
        </Typography>
      </Box>
    </>
  );

  const renderSecurityTab = () => (
    <>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Configure security and privacy settings for this company.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        By default, none of our AI providers can use your data for training.
      </Alert>

      <Box
        sx={{
          p: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          mb: 2,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={zeroDataRetention}
              onChange={(e) => setZeroDataRetention(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="subtitle2">
                Zero Data Retention (ZDR)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Only use AI providers that do not retain your data
              </Typography>
            </Box>
          }
          sx={{ m: 0, alignItems: "flex-start" }}
        />
      </Box>

      <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1, mb: 3 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>About Zero Data Retention:</strong> When enabled, all AI
          requests for this company will only be routed to providers with zero
          data retention policies. This ensures your prompts and responses are
          not stored in memory or retained by the provider for any purpose.
          Note that this may limit which AI models are available.
        </Typography>
      </Box>

      <Box
        sx={{
          p: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          mb: 2,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={mfaRequired}
              onChange={(e) => setMfaRequired(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="subtitle2">
                Require Multi-Factor Authentication (MFA)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All users in this company must set up and verify MFA to access
                the platform
              </Typography>
            </Box>
          }
          sx={{ m: 0, alignItems: "flex-start" }}
        />
      </Box>

      <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>About MFA:</strong> When enabled, all users who are members of
          this company will be required to set up an authenticator app (like
          Google Authenticator or Authy) and enter a verification code each time
          they log in. This significantly increases account security by
          requiring something the user knows (password/email) and something they
          have (their phone).
        </Typography>
      </Box>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={"md"}
      fullWidth={true}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      PaperProps={{
        sx: {
          height: "85vh",
          maxHeight: "85vh",
        },
      }}
    >
      <DialogTitle>
        {!selectedCompany ? "Create Company" : "Edit Company"}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {showSettingsTabs ? (
          <>
            {settingsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {settingsError}
              </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
                  <Tabs
                    value={currentTab}
                    onChange={(_, newValue) => setCurrentTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
                  >
                    <Tab
                      icon={<BusinessIcon />}
                      label="Details"
                      iconPosition="start"
                    />
                    <Tab
                      icon={<TuneIcon />}
                      label="Features"
                      iconPosition="start"
                    />
                    <Tab
                      icon={<CreditCardIcon />}
                      label="Credit Quota"
                      iconPosition="start"
                    />
                    <Tab
                      icon={<LockIcon />}
                      label="Security"
                      iconPosition="start"
                    />
                  </Tabs>

                  {/* Details Tab */}
                  <TabPanel value={currentTab} index={0}>
                    {renderBasicInfoForm()}
                  </TabPanel>

                  {/* Features Tab */}
                  <TabPanel value={currentTab} index={1}>
                    {renderFeaturesTab()}
                  </TabPanel>

                  {/* Credit Quota Tab */}
                  <TabPanel value={currentTab} index={2}>
                    {renderCreditQuotaTab()}
                  </TabPanel>

                  {/* Security Tab */}
                  <TabPanel value={currentTab} index={3}>
                    {renderSecurityTab()}
                  </TabPanel>
                </Box>
          </>
        ) : (
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {renderBasicInfoForm()}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <div style={{ marginTop: 20, marginBottom: 20 }}>{statusText}</div>
        <Button onClick={handleClose}>Cancel</Button>
        {!selectedCompany && (
          <LoadingButton
            loading={modalLoading}
            variant="contained"
            disabled={!validForm() || (companyLimits && !companyLimits.canCreate)}
            onClick={() => createCompanyHandler()}
          >
            {companyLimits && !companyLimits.canCreate
              ? "Limit Reached"
              : "Create"}
          </LoadingButton>
        )}
        {selectedCompany && (
          <LoadingButton
            loading={modalLoading}
            variant="contained"
            disabled={!validForm()}
            onClick={() => editCompany()}
          >
            Save
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
